import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';

type ChainStep = {
  step_index: number;
  kind: string;
  event_id: string;
  event_type: string;
  agent_id: string | null;
  target_type: string | null;
  target_id: string | null;
  recorded_at: string;
  duration_ms: number | null;
  outcome: string | null;
  metadata: Record<string, unknown> | null;
};

export default function CompositionDetail() {
  const { correlationId = '' } = useParams<{ correlationId: string }>();
  const [steps, setSteps] = useState<ChainStep[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setSteps(null); setError(null);
    (async () => {
      try {
        const r = await api.getCompositionChain(correlationId);
        if (!cancelled) setSteps(r.items);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.status === 403) {
          setError('Composition view is restricted to Cadence tier (T1/T2/T3).');
        } else if (err?.status === 503) {
          setError('PKNT supabase proxy is not configured on the Worker.');
        } else {
          setError(err?.message || 'Could not load composition chain.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [correlationId]);

  const started   = steps?.find(s => s.kind === 'started');
  const completed = steps?.find(s => s.kind === 'completed');
  const durationMs = started && completed
    ? new Date(completed.recorded_at).getTime() - new Date(started.recorded_at).getTime()
    : null;

  function toggle(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <Link to="/compositions" className="font-mono text-[11px] uppercase tracking-widest text-ink-light hover:text-accent">
          ← back to compositions
        </Link>
      </div>

      <div className="flex items-baseline justify-between mb-6">
        <h1 className="font-display text-3xl font-medium text-ink">Composition chain</h1>
        {steps && (
          <span className="font-mono text-[11px] uppercase tracking-widest text-ink-light">
            {steps.length} step{steps.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 mb-8 font-mono text-[12px]">
        <dt className="text-ink-light uppercase tracking-widest text-[10px] self-center">correlation_id</dt>
        <dd className="text-ink break-all">{correlationId}</dd>
        {started && (
          <>
            <dt className="text-ink-light uppercase tracking-widest text-[10px] self-center">started_at</dt>
            <dd className="text-ink-soft">{new Date(started.recorded_at).toISOString().replace('T', ' ').slice(0, 19)}</dd>
          </>
        )}
        {completed && (
          <>
            <dt className="text-ink-light uppercase tracking-widest text-[10px] self-center">completed_at</dt>
            <dd className="text-ink-soft">{new Date(completed.recorded_at).toISOString().replace('T', ' ').slice(0, 19)}</dd>
            <dt className="text-ink-light uppercase tracking-widest text-[10px] self-center">outcome</dt>
            <dd className="text-ink-soft">{completed.outcome || '—'}</dd>
          </>
        )}
        {durationMs != null && (
          <>
            <dt className="text-ink-light uppercase tracking-widest text-[10px] self-center">duration</dt>
            <dd className="text-ink-soft">{durationMs} ms</dd>
          </>
        )}
      </dl>

      {error && <p className="font-serif text-accent">{error}</p>}
      {!steps && !error && (
        <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>
      )}
      {steps && steps.length === 0 && !error && (
        <p className="font-serif text-ink-light italic">No events recorded for this correlation_id.</p>
      )}
      {steps && steps.length > 0 && (
        <ol className="border border-rule bg-paper">
          {steps.map((step) => {
            const isOpen = expanded.has(step.event_id);
            return (
              <li key={step.event_id} className="border-t first:border-t-0 border-rule">
                <button
                  type="button"
                  onClick={() => toggle(step.event_id)}
                  className="w-full text-left px-4 py-3 grid grid-cols-[max-content_max-content_1fr_max-content_max-content] gap-x-4 items-baseline font-mono text-[12px] hover:bg-cream-dark/30"
                >
                  <span className="text-ink-light w-6 text-right">{step.step_index}</span>
                  <span className="text-ink-light">
                    {new Date(step.recorded_at).toISOString().slice(11, 19)}
                  </span>
                  <span className="text-ink">
                    <span className={kindBadgeClass(step.kind)}>{step.kind}</span>
                    <span className="ml-2">{step.event_type}</span>
                    {step.agent_id && (
                      <span className="ml-2 text-ink-soft">· {step.agent_id}</span>
                    )}
                    {step.target_type && step.target_id && (
                      <span className="ml-2 text-ink-light">
                        → {step.target_type}:{step.target_id.slice(0, 16)}
                      </span>
                    )}
                  </span>
                  <span className="text-ink-soft">
                    {step.duration_ms != null ? `${step.duration_ms} ms` : ''}
                  </span>
                  <span className={outcomeBadgeClass(step.outcome)}>{step.outcome || ''}</span>
                </button>
                {isOpen && step.metadata && Object.keys(step.metadata).length > 0 && (
                  <pre className="bg-cream-dark/40 px-4 py-3 font-mono text-[11px] text-ink-soft whitespace-pre-wrap break-words border-t border-rule">
{JSON.stringify(step.metadata, null, 2)}
                  </pre>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function kindBadgeClass(k: string): string {
  if (k === 'started')   return 'inline-block px-2 py-0.5 bg-blue-50 text-blue-800 text-[10px] uppercase tracking-wider';
  if (k === 'completed') return 'inline-block px-2 py-0.5 bg-emerald-50 text-emerald-800 text-[10px] uppercase tracking-wider';
  return 'inline-block px-2 py-0.5 bg-cream-dark text-ink-soft text-[10px] uppercase tracking-wider';
}

function outcomeBadgeClass(o: string | null): string {
  if (!o) return '';
  if (o === 'success') return 'inline-block px-2 py-0.5 bg-emerald-50 text-emerald-800 text-[11px]';
  if (o === 'error' || o === 'timeout') return 'inline-block px-2 py-0.5 bg-red-50 text-red-800 text-[11px]';
  return 'inline-block px-2 py-0.5 bg-cream-dark text-ink-soft text-[11px]';
}
